import { Outlet, Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { Home, FileText, Shield, Briefcase, LogOut, Menu, X } from 'lucide-react'
import { useState, useEffect } from 'react'
import NotificationBell from './NotificationBell'
import ToastNotifications from './ToastNotifications'
import { useWebSocket } from '@/services/websocket'

export default function Layout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { connect, disconnect } = useWebSocket()

  // Handle WebSocket connection and mock data
  useEffect(() => {
    if (user) {
      // Connect to WebSocket for real-time notifications
      const token = localStorage.getItem('token') || ''
      if (token) {
        connect(token)
      }
    }

    // Cleanup on unmount
    return () => {
      disconnect()
    }
  }, [user])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Projects', href: '/projects', icon: Briefcase },
    { name: 'Contracts', href: '/contracts', icon: FileText },
    { name: 'Compliance', href: '/compliance', icon: Shield },
  ]

  const billing = (user as { billing?: { trial_active?: boolean; days_left_in_trial?: number; payment_method_on_file?: boolean; requires_payment_setup?: boolean } } | null)?.billing

  return (
    <div className="min-h-screen bg-gray-50">
      {user && billing && !billing.payment_method_on_file && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 py-2 text-center">
          {billing.trial_active
            ? `30-day free trial — ${billing.days_left_in_trial ?? 30} days left. Billing does not start until the trial ends.`
            : 'Your trial has ended. Add a card to keep using Smart Legal Guard.'}{' '}
          <Link to="/billing" className="font-medium underline">
            Manage billing
          </Link>
        </div>
      )}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <Link to="/" className="flex-shrink-0 flex items-center">
                <span className="text-xl font-bold text-primary-600">Smart Legal Guard</span>
              </Link>
              {user && (
                <div className="hidden md:ml-6 md:flex md:space-x-8">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-900"
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center">
              {user ? (
                <div className="flex items-center space-x-4">
                  <NotificationBell />
                  <span className="text-sm text-gray-700">{user.email}</span>
                  <button
                    onClick={handleLogout}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/login"
                    className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Get Started
                  </Link>
                </div>
              )}
              <div className="md:hidden ml-2">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>
        {mobileMenuOpen && user && (
          <div className="md:hidden">
            <div className="pt-2 pb-3 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <item.icon className="inline w-4 h-4 mr-2" />
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
      <main>
        <Outlet />
      </main>
      <ToastNotifications />
    </div>
  )
}